import { ROLES, roleToId } from '../../constants/roles'
import { Close as CloseIcon } from '@mui/icons-material'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { useState, useCallback } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { Link } from 'react-router-dom'

interface CompanyOption {
  readonly id: number
  readonly name: string
}

interface AddUserDialogProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly companies: CompanyOption[]
  readonly onAddUser: (userData: {
    readonly companyId: number
    readonly firstName: string
    readonly lastName: string
    readonly email: string
    readonly phone: string
    readonly occupation: string
    readonly role_id: number
    readonly isPrimaryContact: boolean
  }) => Promise<void>
}

const AddUserDialog = ({
  open,
  onClose,
  companies,
  onAddUser,
}: AddUserDialogProps) => {
  const intl = useIntl()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [title, setTitle] = useState('')
  const [phone, setPhone] = useState('')
  const [role, setRole] = useState('user')
  const [companyId, setCompanyId] = useState<number | ''>('')
  const [isPrimaryContact, setIsPrimaryContact] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const resetForm = useCallback(() => {
    setFirstName('')
    setLastName('')
    setEmail('')
    setTitle('')
    setPhone('')
    setRole('user')
    setCompanyId('')
    setIsPrimaryContact(false)
  }, [])

  const handleClose = useCallback(() => {
    resetForm()
    onClose()
  }, [onClose, resetForm])

  const handleAddUser = useCallback(() => {
    if (companyId === '') return
    setSubmitting(true)
    void onAddUser({
      companyId,
      firstName,
      lastName,
      email,
      phone,
      occupation: title,
      role_id: roleToId(role),
      isPrimaryContact,
    })
      .then(() => handleClose())
      .finally(() => setSubmitting(false))
  }, [
    onAddUser,
    companyId,
    firstName,
    lastName,
    email,
    phone,
    title,
    role,
    isPrimaryContact,
    handleClose,
  ])

  const isFormValid =
    firstName.trim() !== '' &&
    lastName.trim() !== '' &&
    email.trim() !== '' &&
    companyId !== ''

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { backgroundColor: '#31353D', backgroundImage: 'none' },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <FormattedMessage id="users.addUser" />
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <FormControl fullWidth>
            <InputLabel id="add-user-org-label">
              {intl.formatMessage({ id: 'users.addUser.organization' })}
            </InputLabel>
            <Select
              labelId="add-user-org-label"
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value as number)}
              label={intl.formatMessage({ id: 'users.addUser.organization' })}
            >
              {companies.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: 'block' }}
            >
              <FormattedMessage id="users.addUser.orgHint" />{' '}
              <Link
                to="/organizations/new"
                style={{ color: '#9EC73E', textDecoration: 'none' }}
              >
                <FormattedMessage id="users.addUser.orgHintLink" />
              </Link>
              .
            </Typography>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={intl.formatMessage({ id: 'users.addUser.firstName' })}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={intl.formatMessage({
                id: 'users.addUser.firstNamePlaceholder',
              })}
              fullWidth
            />
            <TextField
              label={intl.formatMessage({ id: 'users.addUser.lastName' })}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder={intl.formatMessage({
                id: 'users.addUser.lastNamePlaceholder',
              })}
              fullWidth
            />
          </Box>
          <TextField
            label={intl.formatMessage({ id: 'users.addUser.title' })}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'users.addUser.titlePlaceholder',
            })}
            fullWidth
          />
          <TextField
            label={intl.formatMessage({ id: 'users.addUser.email' })}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'users.addUser.emailPlaceholder',
            })}
            fullWidth
          />
          <TextField
            label={intl.formatMessage({ id: 'users.addUser.phone' })}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'users.addUser.phonePlaceholder',
            })}
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel id="add-user-role-label">
              {intl.formatMessage({ id: 'users.addUser.role' })}
            </InputLabel>
            <Select
              labelId="add-user-role-label"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              label={intl.formatMessage({ id: 'users.addUser.role' })}
            >
              {ROLES.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={isPrimaryContact}
                onChange={(e) => setIsPrimaryContact(e.target.checked)}
              />
            }
            label={intl.formatMessage({
              id: 'users.addUser.setPrimaryContact',
            })}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          <FormattedMessage id="users.confirmDelete.cancel" />
        </Button>
        <Button
          onClick={handleAddUser}
          variant="contained"
          disabled={!isFormValid || submitting}
        >
          {submitting
            ? intl.formatMessage({ id: 'users.addUser.adding' })
            : intl.formatMessage({ id: 'users.addUser' })}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddUserDialog
